import type { CommonResources } from '../en/common';

// Typed against the English shape: German must carry exactly the same keys.
// Single-brace `{count}`/`{column}` tokens are filled by the table framework,
// not i18next — keep them; `{{double}}` braces are interpolated by i18next.
export const common: CommonResources = {
  loading: 'Wird geladen…',

  table: {
    searchPlaceholder: 'Suchen …',
    clearSearch: 'Suche zurücksetzen',
    filters: 'Filter',
    columns: 'Spalten',
    allOf: 'Alle {{label}}',
    clearAllFilters: 'Alle zurücksetzen',
    clearFilters: 'Filter zurücksetzen',
    filtersActiveOne: '1 Filter aktiv',
    filtersActiveMany: '{count} Filter aktiv',
    filterByColumn: 'Nach {column} filtern',
    filterSearchPlaceholder: '{column} durchsuchen …',
    resetFilter: 'Filter zurücksetzen',
    filterNoMatch: 'Kein Treffer.',
    filterMoreOptions: '+{count} weitere – Suche eingrenzen, um sie zu sehen.',
    toggleColumns: 'Spalten ein-/ausblenden',
    columnAlwaysVisible: 'Immer sichtbar',
    resetColumns: 'Auf Standard zurücksetzen',
    searchColumns: 'Spalten durchsuchen…',
    noColumnsFound: 'Keine Spalten gefunden.',
    noResults: 'Keine Ergebnisse',
    noResultsDescription: 'Passen Sie Suche oder Filter an.',
    loadFailed: 'Laden fehlgeschlagen.',
    retry: 'Erneut versuchen',
    rowsPerPage: 'Zeilen pro Seite',
    pageOf: 'Seite {{current}} von {{total}}',
    previousPage: 'Vorherige Seite',
    nextPage: 'Nächste Seite',
    resultOne: 'Ergebnis',
    resultOther: 'Ergebnisse',
    resultsSuffix: 'gefunden',
    export: 'CSV exportieren',
    exporting: 'Exportieren …',
    exportTooLarge: 'Zu viele Zeilen für den Export. Filter eingrenzen und erneut versuchen.',
  },

  listView: {
    rename: 'Umbenennen',
    delete: 'Löschen',
    deleteViewTitle: 'Ansicht „{{name}}“ löschen?',
    deleteViewDescription: 'Damit wird die gespeicherte Ansicht entfernt. Ihre aktuelle Liste bleibt unverändert.',
    cancel: 'Abbrechen',
    renameDialogTitle: 'Ansicht umbenennen',
    viewNamePlaceholder: 'Ansichtsname',
    save: 'Speichern',
    saveCurrentViewTitle: 'Aktuelle Ansicht speichern',
    saveViewButton: 'Ansicht speichern',
    optionsFor: 'Optionen für {{name}}',
    viewFallback: 'Ansicht',
  },

  accessDenied: {
    title: 'Sie haben keinen Zugriff auf diese Seite',
    description:
      'Ihrem Konto fehlt eine Berechtigung, die diese Seite voraussetzt. Eine Administratorin oder ein Administrator Ihrer Organisation kann sie erteilen.',
    back: 'Zurück zur Übersicht',
  },

  fieldErrors: {
    brandMissing: 'Marke ist ein Pflichtfeld.',
    modelMissing: 'Modell ist ein Pflichtfeld.',
    countryCodeMissing: 'Zulassungsland ist ein Pflichtfeld.',
    drivetrainMissing: 'Antrieb ist ein Pflichtfeld.',
    isNotEmpty: 'Pflichtfeld',
    isEmail: 'Geben Sie eine gültige E-Mail-Adresse ein',
    duplicatedVin: 'Es existiert bereits ein Fahrzeug mit dieser FIN.',
    duplicatedVinInFleet: 'Diese FIN gehört bereits zu {{vehicle}} ({{plate}}).',
    hsnInvalidFormat: 'Die HSN besteht aus genau 4 Ziffern — führende Nullen mit angeben.',
    tsnInvalidFormat: 'Die TSN besteht aus genau 3 Buchstaben oder Ziffern.',
    plateInvalidLength: 'Das Kennzeichen muss 2 bis 15 Zeichen lang sein.',
    plateInvalidCharacters: 'Erlaubt sind nur Buchstaben, Ziffern, Leerzeichen und Bindestriche.',
    mileageDateInFuture: 'Der Stichtag kann nicht in der Zukunft liegen.',
    mileageDateBeforeFirstRegistration: 'Liegt vor der Erstzulassung.',
    productionDateInFuture: 'Das Herstellungsdatum kann nicht in der Zukunft liegen.',
    doorsOutOfRange: 'Türen muss zwischen 1 und 6 liegen.',
    seatsOutOfRange: 'Sitzplätze muss zwischen 1 und 9 liegen.',
    powerNotPositive: 'Die Leistung muss größer als 0 kW sein.',
    enginePowerAboveSystemPower:
      'Die Verbrennerleistung kann nicht über der Systemleistung liegen — sie ist ein Teil davon.',
    displacementNotPositive: 'Der Hubraum muss größer als 0 cm³ sein.',
    consumptionNegative: 'Der Verbrauch kann nicht negativ sein.',
    electricConsumptionNegative: 'Der Stromverbrauch kann nicht negativ sein.',
    co2Negative: 'Der CO₂-Ausstoß kann nicht negativ sein.',
    adBlueCapacityNotPositive: 'Das AdBlue-Volumen muss größer als 0 l sein.',
    storageCapacityNotPositive: '{{storage}}: {{source}} braucht eine Kapazität größer als 0 {{unit}}.',
    consumptionEntryNegative: '{{source}}: Der Verbrauch kann nicht negativ sein.',
    wheelTireCombinationMissingSize: 'Eine Rad-/Reifenkombination braucht eine Reifengröße.',
    rangeReversed: 'Der größte Wert liegt unter dem kleinsten.',
    grossMassBelowKerbWeight:
      'Die zulässige Gesamtmasse kann nicht unter dem Leergewicht liegen — daraus ergäbe sich eine negative Nutzlast.',
    displacementNotApplicable: 'Ein rein elektrisches Fahrzeug hat keinen Hubraum.',
    co2NotApplicable: 'Ein Elektrofahrzeug hat 0 g/km CO₂ am Auspuff.',
    adBlueNotApplicable: 'Dieser Antrieb führt kein AdBlue.',
    fieldNotApplicableForDrivetrain: 'Dieses Feld gibt es bei diesem Antrieb nicht.',
    derivedValueMismatch: 'Passt nicht zum Antriebskonzept.',
    conflictsWithHiddenFields: 'Steht im Widerspruch zu Feldern, auf die Sie keinen Zugriff haben.',
    serviceDateBeforeRegistration: 'Die letzte Wartung darf nicht vor der Erstzulassung liegen.',
    writeForbidden: 'Sie dürfen dieses Feld nicht ändern.',
    duplicatedLicensePlate: 'Es existiert bereits ein aktives Fahrzeug mit diesem Kennzeichen.',
    invalidVin: 'Geben Sie eine gültige 17-stellige FIN ein (die Buchstaben I, O und Q werden nie verwendet).',
    missingRequiredField: 'Pflichtfeld',
    orgUnitNotInTenant: 'Diese Organisationseinheit gehört nicht zu Ihrer Organisation.',
    groupNotInTenant: 'Diese Gruppe gehört nicht zu Ihrer Organisation.',
    mileageExceedsMaximum: 'Der Kilometerstand darf 2.000.000 km nicht überschreiten.',
    mileageBelowMinimum: 'Der Kilometerstand darf nicht negativ sein.',
    mileageDecreaseRequiresReason: 'Eine Verringerung des Kilometerstands erfordert einen Korrekturgrund.',
    duplicatedLocationName: 'Ein Standort mit diesem Namen existiert bereits',
    assignmentStartInFuture: 'Die Zuweisung kann nicht in der Zukunft beginnen.',
    driverAssignmentOverlap:
      'Die Zuweisung kann nicht beginnen, bevor die vorherige Zuweisung des Fahrers geendet hat.',
    vehicleAssignmentOverlap:
      'Die Zuweisung kann nicht beginnen, bevor die vorherige Zuweisung des Fahrzeugs geendet hat.',
    contractPositionDateOrder: 'Das Datum "Gültig bis" darf nicht vor "Gültig ab" liegen.',
    contractPositionOverlap:
      'Dieser Zeitraum überschneidet sich mit einem anderen Zeitraum dieses Fahrzeugs auf diesem Vertrag.',
  },
};
